export type BillingMetricType = "session" | "client";

export type BillingTierName = "starter" | "integration" | "growth";

export type BillingThresholdState =
    | "active"
    | "warning_80"
    | "warning_100"
    | "blocked";

export type BillingBlockReason =
    | "trial_hard_stop"
    | "free_tier_hard_stop"
    | "paid_plan_over_grace";

export interface BillingBlockedPayload {
    code: "BILLING_LLM_BLOCKED";
    message: string;
    enforced: boolean;
    metricType: BillingMetricType | null;
    thresholdState: BillingThresholdState | "blocked";
    blockReason: BillingBlockReason | null;
    usedUnits: number | null;
    limitUnits: number | null;
}

export interface BillingEntitlement {
    billableAccountId: string;
    metricType: BillingMetricType;
    cycleStartAt: string;
    cycleEndAt: string;
    usedUnits: number;
    limitUnits: number;
    warning80Reached: boolean;
    warning100Reached: boolean;
    blocked: boolean;
    thresholdState: BillingThresholdState;
    blockReason: BillingBlockReason | null;
}

export interface BillingEntitlementResponse {
    enforced: boolean;
    entitlement: BillingEntitlement | null;
}

export interface BillingFeatureUsageCounter {
    usedUnits: number;
    limitUnits: number | null;
    remainingUnits: number | null;
    blocked: boolean;
}

export interface BillingFeatureUsageSummary {
    billableAccountId: string;
    tierName: BillingTierName;
    cycleStartAt: string;
    cycleEndAt: string;
    soapNotesEnabled: boolean;
    voiceCloneEnabled: boolean;
    assessments: BillingFeatureUsageCounter;
    practitionerChatMessages: BillingFeatureUsageCounter;
}

export interface BillingPolicySummary {
    id: string;
    billableAccountId: string;
    metricType: BillingMetricType;
    monthlyLimitUnits: number;
    gracePercent: number;
    hardStopAtLimit: boolean;
    isTrial: boolean;
    tierName: BillingTierName | null;
    stripeSubscriptionId: string | null;
    stripePriceId: string | null;
    pendingChange: {
        metricType: BillingMetricType;
        monthlyLimitUnits: number;
        gracePercent: number;
        effectiveAt: string;
        reason: string | null;
    } | null;
}

export interface BillingUsageEventSummary {
    id: string;
    metricType: BillingMetricType;
    units: number;
    sourceType: string;
    sourceId: string;
    relatedClientId: string | null;
    metadata: Record<string, unknown> | null;
    createdAt: string;
}

export interface BillingThresholdEventSummary {
    id: string;
    thresholdState: "warning_80" | "warning_100" | "blocked";
    usedUnits: number;
    limitUnits: number;
    createdAt: string;
}

export interface BillingAuditActionSummary {
    id: string;
    actorAdminId: string;
    actionType: "admin_adjustment" | "policy_change";
    reason: string | null;
    before: Record<string, unknown> | null;
    after: Record<string, unknown> | null;
    createdAt: string;
}

export interface BillingAiCostSummary {
    id: string;
    sessionId: string | null;
    operationType: string;
    modelUsed: string;
    costUsd: string;
    inputTokens: number;
    outputTokens: number;
    cacheCreationInputTokens: number | null;
    cacheReadInputTokens: number | null;
    durationMs: number;
    createdAt: string;
}

export interface BillingAiCostCategoryBreakdown {
    category: string;
    calls: number;
    costUsd: number;
}

export interface BillingAiCostTotals {
    totalCostUsd: string;
    totalCalls: number;
    totalSessions: number;
}

export interface BillingAiCostTab {
    totals: BillingAiCostTotals;
    byCategory: BillingAiCostCategoryBreakdown[];
}

export interface BillingStripeDisplaySummary {
    planName: string | null;
    priceAmountLabel: string | null;
    subscriptionUrl: string | null;
    priceUrl: string | null;
}

// AIDEV-NOTE: supportedPricing summarizes per-account PWYC price state from Stripe catalog.
export interface AdminSupportedPricingSummary {
    exists: boolean;
    activePriceId: string | null;
    baseTier: BillingTierName | null;
    unitAmountCents: number | null;
}

export interface BillingRefundablePayment {
    chargeId: string;
    paymentIntentId: string | null;
    createdAt: string;
    description: string | null;
    currency: string;
    amountCents: number;
    amountRefundedCents: number;
    amountRefundableCents: number;
    receiptUrl: string | null;
}

export interface AdminStaleStripeSubscriptionSummary {
    subscriptionId: string;
    status: string;
    priceIds: string[];
    customerId: string | null;
    dashboardUrl: string;
}

export interface BillingRefundPaymentsSummary {
    refundedCount: number;
    skippedCount: number;
    failedCount: number;
    totalRefundedCents: number;
}

export interface BillingRefundPaymentResult {
    chargeId: string;
    status: "refunded" | "skipped" | "failed";
    refundedAmountCents: number;
    message: string;
}

export interface AdminBillingAccountSnapshot {
    policy: BillingPolicySummary;
    entitlement: BillingEntitlement;
    cycleStartAt: string;
    cycleEndAt: string;
    recentAdjustments: BillingUsageEventSummary[];
    recentThresholdEvents: BillingThresholdEventSummary[];
    recentAuditActions: BillingAuditActionSummary[];
    aiCostLifetime: BillingAiCostTab;
    aiCostCycle: BillingAiCostTab;
    stripeDisplay?: BillingStripeDisplaySummary;
    supportedPricing?: AdminSupportedPricingSummary;
    refundablePayments?: BillingRefundablePayment[];
    staleStarterSubscriptions?: AdminStaleStripeSubscriptionSummary[];
    // AIDEV-NOTE: featureUsage covers per-cycle assessment, chat (single + multi-session/consultant),
    // and SOAP-notes availability so support can see all entitlement-adjacent usage on one page.
    featureUsage?: BillingFeatureUsageSummary | null;
}

export interface AdminReferralAttributionSummary {
    referredProfileId: string;
    affiliateId: string;
    basis: string;
    decidedAt: string;
    drivingEventId: string | null;
    affiliate: {
        id: string;
        profileId: string;
        programType: "referral" | "affiliate";
        payoutOption: string;
        status: string;
        defaultLinkSlug: string;
        email: string | null;
        fullName: string | null;
    } | null;
}

// AIDEV-NOTE: Preserve server payload details across hooks/components so all blocked surfaces stay consistent.
export class BillingLlmBlockedError extends Error {
    readonly billing: BillingBlockedPayload;

    constructor(billing: BillingBlockedPayload) {
        super(billing.message);
        this.name = "BillingLlmBlockedError";
        this.billing = billing;
    }
}

export function isBillingLlmBlockedError(
    error: unknown,
): error is BillingLlmBlockedError {
    return error instanceof BillingLlmBlockedError;
}

// AIDEV-NOTE: What switch-plan did with a pending voice after an in-place upgrade. Shared by
// the route (api.billing.switch-plan) and the voice paywall (UpgradePlansDialog / VoicesCard).
export type PendingVoiceOutcome =
    | "assigned"
    | "not_entitled"
    | "not_found"
    | "not_ready"
    | "not_a_practitioner"
    | "failed";
